import { useCallback, useContext, useState } from "react";
import clsx from "clsx";
import { ProfileContext } from "src/providers/profile-provider";
import Layout from "src/components/Layout/Layout";
import { useLogout } from "src/hooks/useLogout";
import Notification from "src/components/Notification/Notification";
import styles from "./Connected-acounts.module.css";
import formStyles from "src/components/styles/form.module.css";
import { useGetUserLinkedAccounts } from "src/hooks/useGetUserLinkedAccounts";
import { ConnectedAccountsTable } from "./Connected-acounts-table";
import Button from "src/components/Button/TrackedButton";
import { FormInput } from "@k8slens/lds-form";
import { Modal } from "@k8slens/lds";
import { businessHandleValidation, LensSDKException } from "lens-platform-sdk";
import { defaultErrorParser } from "src/components/FormComponents/FormSelect";
import { useLensPlatformClient } from "src/hooks/useLensPlatformClient";

export const ConnectedAcounts = () => {
  const { profile } = useContext(ProfileContext);
  const logout = useLogout();
  const flashDuration = 1500;
  const [isUsernameModalOpen, setIsUsernameModalOpen] = useState(false);
  const [businessHandle, setBusinessHandle] = useState({ value: "", valid: false });
  const lensPlatformClient = useLensPlatformClient();
  const [error, setError] = useState<LensSDKException | null>(null);
  const {
    userLinkedAccounts,
    deleteUserLinkedAccount,
    errorDeleteUserLinkedAccount,
    loadingUserLinkedAccounts,
    deleteUserLinkedAccountLoading,
  } = useGetUserLinkedAccounts();

  const linkProviderAccount = useCallback(
    async (providerAlias: string) => {
      const connectLink = await lensPlatformClient.sso.getSSOProviderConnectionLink(providerAlias, "lensCloudFrontend");
      const target = "_blank";

      if (connectLink.link) {
        window.open(connectLink.link, target);
      }
    },
    [lensPlatformClient],
  );

  const validateHandle = useCallback((value: string) => {
    if (!businessHandleValidation.test(value)) {
      return "patternMismatch";
    }

    return undefined;
  }, []);

  const handleInputError = useCallback((value: string, error: string) => {
    if (error === "patternMismatch") {
      return "Allowed characters A-Z, a-z, 0-9, -, Minimum length is 3 and maximum length is 15 characters. Should start with a letter.";
    }

    return defaultErrorParser(
      value,
      error,
      {
        minLength: 3,
      },
      "",
    );
  }, []);

  const handleConnectBusinessAccount = async () => {
    if (businessHandle.value && businessHandle.valid) {
      try {
        const businessSSO = await lensPlatformClient.sso.getSSOByBusinessHandle(businessHandle.value);

        if (businessSSO && businessSSO.identityProviderID) {
          linkProviderAccount(businessSSO.identityProviderID);
        }
      } catch (error) {
        if (error instanceof LensSDKException) {
          setError(error);
        }
      }
    }
  };

  const openEditUsernameModal = () => {
    setIsUsernameModalOpen(true);
  };

  const handleCloseUsernameModal = () => {
    setIsUsernameModalOpen(false);
  };

  const onUnlinkAccount = (identityProviderAlias: string | undefined) => {
    if (!identityProviderAlias) {
      return;
    }

    deleteUserLinkedAccount(identityProviderAlias);
  };

  return (
    <Layout className="bg-secondary" profile={profile} logout={logout}>
      <div className={styles.linkedAccounts}>
        {(errorDeleteUserLinkedAccount || error) && (
          <Notification
            flashDuration={flashDuration}
            level="error"
            message={errorDeleteUserLinkedAccount?.message || error?.message}
            type="flash"
          />
        )}
        <section className={styles.linkedAccountsContent}>
          <div>
            <h2 className={clsx("lds-h2-caps", styles.welcome)}>Connected accounts</h2>
            <p>Connect your existing third-party service accounts with Lens ID.</p>
          </div>
          <section className={styles.accountsList}>
            <ConnectedAccountsTable
              loading={loadingUserLinkedAccounts || deleteUserLinkedAccountLoading}
              accounts={userLinkedAccounts}
              onDeleteAccount={onUnlinkAccount}
            />
          </section>
        </section>
        <section>
          <Button
            label="Connect new account"
            className={styles.connectButton}
            primary
            onClick={openEditUsernameModal}
          />
        </section>
      </div>

      <Modal
        title="Connect new account"
        size="md"
        onClose={handleCloseUsernameModal}
        isOpen={isUsernameModalOpen}
        className={styles.modalWithForm}
        contentComponent="form"
        footer={
          <>
            <Button label="Cancel" id="cancel-connect-account" onClick={handleCloseUsernameModal} />
            <Button
              label="Connect account"
              disabled={!businessHandle.valid}
              id="connect-account"
              onClick={handleConnectBusinessAccount}
              primary
            />
          </>
        }
      >
        <div className={styles.social}>
          <p>Choose social account</p>
          <div className={styles.socialButtons}>
            <Button
              className={formStyles.githubBtn}
              label="GitHub"
              onClick={(e) => {
                e.preventDefault();
                linkProviderAccount("github");
              }}
            />
            <Button
              className={formStyles.googleBtn}
              label="Google"
              onClick={(e) => {
                e.preventDefault();
                linkProviderAccount("google");
              }}
            />
          </div>
        </div>
        <div className={styles.businessHandle}>
          <p>Connect company account</p>
          <FormInput
            label="Business ID"
            id="businessHandle"
            name="businessHandle"
            value={businessHandle.value}
            validate={validateHandle}
            errorParser={handleInputError}
            onChange={(value, valid) => setBusinessHandle({ value, valid })}
          />
        </div>
      </Modal>
    </Layout>
  );
};
